import { ArrowRightOnRectangleIcon, ArrowsRightLeftIcon, ClockIcon, PencilIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { ReallocateInvoiceModal, RefundModal } from 'components/Modals'
import { StripeHistoryModal } from 'components/Modals/Payment/StripeHistoryModal'
import { Overview } from 'components/Overview'
import { AccountType, RefundReason, userHasAccountType } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import { LoanProcessRushPaymentRequestTypeTitles } from 'pages/LoanSubmission/constants'
import { InvoicePaymentModal } from 'pages/LoanSubmission/Modals/InvoicePaymentModal'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { partiesMapWithAccountTypes } from 'reducers/loanDetail.reducer'
import {
  getInvoices,
  getPaymentIntentDashboardUrl,
  IStripeIntent,
  PaymentOrderType,
  PaymentOrderTypeTitles,
  PaymentRefIdTitles,
  reallocateInvoice,
  refundInvoice,
  setInvoiceStatusLabel,
  StripeIntentStatus,
} from 'services'
import { Button, Modal, Tooltip } from 'stories/components'
import { borrowerEmailParties, confirm, formatTime, getPrice2decimal, renderHeader, thousandSeperator } from 'utils'
import { setLoanNumber } from 'utils/setLoanNumber'

import { StripeStatusLabels } from '../OrderAppraisals'

/**
 * One row of the invoice listing. The backend emits a payment as one line and each refund out of
 * it as its own `refund` line (negative amount, dated when it was issued), so the payment's row
 * always reads as it was charged. `rawStatus` is the intent's stored status, which still says
 * refunded/partially refunded; that is what decides whether more can be refunded.
 */
type InvoiceLine = Omit<IStripeIntent, 'id'> & {
  id: number | string
  lineType?: 'payment' | 'refund'
  paymentIntentId?: string
  rawStatus?: string
  refundedAmount?: number
  remainingAmount?: number
  refund?: { reason: string; description: string; by: string; partial: boolean }
}

const REALLOCATABLE_ORDER_TYPES = [PaymentOrderType.LoanSubmissionRush, PaymentOrderType.CounterPartyApproval]

export default function Invoices({ child = false }: { child?: boolean }) {
  const { borrower, auth, loanDetail } = useSelector((state: any) => ({
    borrower: state.borrower.borrower,
    auth: state.auth,
    loanDetail: state.loanDetail,
  }))

  const emailParties = useMemo(() => {
    const partiesMap = partiesMapWithAccountTypes()
    const result: { party: string; email: string }[] = []
    // One row per borrower address, so a refund notice can reach every address on file rather than
    // only the primary.
    result.push(...borrowerEmailParties(borrower))
    const parties = loanDetail?.parties || {}
    Object.keys(parties).forEach((key) => {
      const { email } = parties[key] || {}
      if (email) result.push({ party: partiesMap[key] || key, email })
    })
    return result
  }, [borrower, loanDetail])
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [data, setData] = useState<InvoiceLine[]>([])
  const [historyItem, setHistoryItem] = useState<Record<string, any>>({})
  const [showHistory, setShowHistory] = useState<boolean>(false)
  const [showRefund, setShowRefund] = useState(false)
  const [refundItem, setRefundItem] = useState<Record<string, any>>({})
  const [reallocateItem, setReallocateItem] = useState<InvoiceLine | null>(null)
  const [labelItem, setLabelItem] = useState<InvoiceLine | null>(null)

  const isAdmin = userHasAccountType(auth.profile, AccountType.ADMIN)
  const { hasPermission } = usePermissions()
  const canReallocate = hasPermission('Loan.Invoice.Reallocate')
  const canOverrideStatusLabel = hasPermission('Loan.Invoice.StatusLabel')
  useEffect(() => {
    setLoanNumber()
    refetch()
  }, [])

  const refetch = () => {
    setLoading(true)
    getInvoices()
      .then((data) => setData(data))
      .finally(() => setLoading(false))
  }

  const onAdd = () => {
    setIsOpen(true)
  }

  const onCloseModal = () => {
    setIsOpen(false)
    refetch()
  }

  const sortableHeaders = [
    { title: 'Intent ID', key: 'intentId' },
    { title: 'Customer Email', key: 'email' },
    { title: 'Type', key: 'type' },
    { title: 'Amount', key: 'amount' },
    { title: 'Status', key: 'status' },
    { title: 'Detail', key: 'detail' },
    { title: 'Created At', key: 'createdAt' },
  ]

  const feeTitle = (item: InvoiceLine) =>
    LoanProcessRushPaymentRequestTypeTitles[item.data?.requestType] || item.data?.feeTitle

  const renderOrderType = (item: InvoiceLine) => {
    if (item.lineType === 'refund') return `Refund - ${feeTitle(item) || PaymentOrderTypeTitles[item.orderType]}`
    if (item.orderType == PaymentOrderType.LoanSubmissionRush) return feeTitle(item)
    return PaymentOrderTypeTitles[item.orderType]
  }

  const renderDetails = (item: InvoiceLine) => {
    if (item.lineType === 'refund') {
      const { reason, description, by, partial } = item.refund || ({} as NonNullable<InvoiceLine['refund']>)
      return (
        <div>
          <p>
            {partial ? 'Partial refund' : 'Refund'} of {item.paymentIntentId || item.intentId}
          </p>
          {reason ? <p>Reason: {RefundReason[reason] || reason}</p> : null}
          {description ? <p>{description}</p> : null}
          {by ? <p>By: {by}</p> : null}
        </div>
      )
    }

    const orderType = renderOrderType(item) // PaymentOrderTypeTitles[item.orderType]

    const refTitle = PaymentRefIdTitles[item.orderType]
    const reallocatedFrom = item.data?.reallocatedFrom
    const previousTitle = reallocatedFrom
      ? LoanProcessRushPaymentRequestTypeTitles[reallocatedFrom.requestType] || reallocatedFrom.feeTitle
      : ''

    return (
      <div>
        <p>Order Type: {orderType}</p>
        {!!refTitle && (
          <p>
            {refTitle}: {item.refId}
          </p>
        )}
        {item.data?.requestedBy ? <p>By: {item.data.requestedBy}</p> : null}
        {previousTitle ? (
          <p className="text-xs text-gray-500" title={item.data?.reallocationReason || ''}>
            Reallocated from {previousTitle}
            {item.data?.reallocatedBy ? ` by ${item.data.reallocatedBy}` : ''}
          </p>
        ) : null}
        {!!item.refundedAmount && (
          <p className="text-xs text-gray-500">
            Refunded ${getPrice2decimal(item.refundedAmount, false, true)} · Remaining $
            {getPrice2decimal(item.remainingAmount || 0, false, true)}
          </p>
        )}
      </div>
    )
  }

  const refund = async (intentId: string, refundData: Record<string, any>) => {
    const res: any = await confirm('Are you sure you want to refund this payment?')
    if (res === false) return
    setShowRefund(false)
    await refundInvoice(intentId, refundData)
    refetch()
  }

  const reallocate = async (intentId: string, body: { requestType: string; feeTitle?: string; reason: string }) => {
    await reallocateInvoice(intentId, body)
    toast('Payment reallocated.', { type: 'success' })
    setReallocateItem(null)
    refetch()
  }

  const saveStatusLabel = async (intentId: string, statusLabel: string) => {
    await setInvoiceStatusLabel(intentId, statusLabel)
    toast(statusLabel.trim() ? 'Status label updated.' : 'Status label cleared.', { type: 'success' })
    setLabelItem(null)
    refetch()
  }

  const canRefundLine = (item: InvoiceLine) =>
    isAdmin &&
    item.lineType !== 'refund' &&
    [StripeIntentStatus.succeeded, StripeIntentStatus.partially_refunded].includes(
      (item.rawStatus || item.status) as StripeIntentStatus,
    ) &&
    (item.remainingAmount === undefined || item.remainingAmount > 0)

  /**
   * History belongs to the charge. A refund line carries the refund's own (negative) amount and
   * date, so opening history from one has to borrow the payment line — otherwise "Succeeded"
   * reads as a negative charge and "Created" is dated when the money came back.
   * A full refund recorded before amounts were stored has none; the refund line already holds
   * the inferred amount, so the history row can show it.
   */
  const openHistory = (item: InvoiceLine) => {
    const payment =
      item.lineType === 'refund'
        ? data.find((row) => row.lineType !== 'refund' && row.intentId === item.intentId) || item
        : item
    const refundLines = data.filter((row) => row.lineType === 'refund' && row.intentId === payment.intentId)
    const used = new Set<string | number>()
    const history = (Array.isArray(payment.history) ? payment.history : []).map((entry: any) => {
      if (![StripeIntentStatus.refunded, StripeIntentStatus.partially_refunded].includes(entry.status)) return entry
      if (Number(entry.amount) > 0) return entry
      const line = refundLines.find((row) => !used.has(row.id) && Number(row.createdAt) === Number(entry.date))
      if (!line) return entry
      used.add(line.id)
      return { ...entry, amount: Math.abs(Number(line.amount)) }
    })
    setHistoryItem({ ...payment, history })
    setShowHistory(true)
  }

  const canReallocateLine = (item: InvoiceLine) =>
    canReallocate &&
    item.lineType !== 'refund' &&
    !!item.intentId &&
    !!item.data &&
    REALLOCATABLE_ORDER_TYPES.includes(item.orderType) &&
    [StripeIntentStatus.succeeded, StripeIntentStatus.partially_refunded, StripeIntentStatus.refunded].includes(
      (item.rawStatus || item.status) as StripeIntentStatus,
    )

  return (
    <div className={`invoices-container px-2 py-6`}>
      {!child && <Overview title="Invoices" />}
      <div className="max-w-screen-2xl m-auto">
        <div className={`relative bg-white ${child ? '' : 'shadow1 rounded mb-4 p-4'}`}>
          <LayoutLoading show={loading} />
          <div className="flex gap-4 my-2 justify-between items-center">
            <div className="ml-2 text-[18px] font-variation-settings-600">Invoices</div>
            <div className="">
              <Button onClick={onAdd} className="px-[36px]">
                Add
              </Button>
            </div>
          </div>
          <div className="parties-container overflow-auto mb-6 shadow-md sm:rounded-lg">
            <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
              <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th scope="col" className="px-6 py-3">
                    No
                  </th>
                  {sortableHeaders.map(({ title, key }, index) =>
                    renderHeader({
                      title,
                      sortable: false,
                      key,
                      sortOrder: 0,
                      index,
                      onSort: () => {},
                    }),
                  )}
                  <th scope="col" className="px-6 py-3">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="text-[14.5px] text-gray-900">
                {data.map((item, index) => {
                  const status = StripeStatusLabels[item.status] || []
                  const isRefund = item.lineType === 'refund'
                  const statusText = !isRefund && item.statusLabel ? item.statusLabel : status[1] || item.status
                  const canEditStatusLabel =
                    canOverrideStatusLabel && !isRefund && item.lineType === 'payment' && !!item.intentId
                  return (
                    <tr key={item.id ?? index} className={`border-b ${index % 2 ? 'bg-gray-50' : ''}`}>
                      <td className="px-6 py-3">{index + 1}</td>
                      <td className="px-2">
                        <a
                          className="font-bold text-shade-blue hover:underline cursor-pointer"
                          href={getPaymentIntentDashboardUrl(item)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <span>{item.intentId}</span>
                        </a>
                      </td>
                      <td className="p-2">{item.email}</td>
                      <td className="p-2">{renderOrderType(item)}</td>
                      <td className={`p-2 whitespace-nowrap ${isRefund ? 'text-red-600' : ''}`}>
                        {item.amount < 0 ? '-' : ''}${thousandSeperator(Math.abs(item.amount))}
                      </td>
                      <td className={`p-2 text-${status[0]}-500`}>
                        <span className="inline-flex items-center gap-1">
                          <span>{statusText}</span>
                          {canEditStatusLabel && (
                            <Tooltip message="Override status label">
                              <span
                                className="text-gray-400 cursor-pointer hover-shadow1 p-1 rounded"
                                onClick={() => setLabelItem(item)}
                              >
                                <PencilIcon className="w-3.5 h-3.5" />
                              </span>
                            </Tooltip>
                          )}
                        </span>
                      </td>
                      <td className="p-2">{renderDetails(item)}</td>
                      <td className="p-2">{formatTime(item.createdAt)}</td>
                      <td className="p-2 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {canRefundLine(item) && (
                            <Tooltip message="Refund">
                              <span
                                className="text-red-700 cursor-pointer hover-shadow1 p-1 rounded"
                                onClick={() => {
                                  setShowRefund(true)
                                  setRefundItem(item)
                                }}
                              >
                                <ArrowRightOnRectangleIcon className="w-4 h-4" />
                              </span>
                            </Tooltip>
                          )}
                          {canReallocateLine(item) && (
                            <Tooltip message="Reallocate to a different fee">
                              <span
                                className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded"
                                onClick={() => setReallocateItem(item)}
                              >
                                <ArrowsRightLeftIcon className="w-4 h-4" />
                              </span>
                            </Tooltip>
                          )}
                          {item.intentId && (
                            <span
                              className="text-gray-500 cursor-pointer hover-shadow1 p-1 rounded"
                              onClick={() => openHistory(item)}
                            >
                              <ClockIcon className="w-4 h-4" />
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {showHistory && (
        <StripeHistoryModal
          history={historyItem.history}
          createdAt={historyItem.createdAt}
          email={historyItem.email}
          amount={historyItem.amount}
          onClose={() => setShowHistory(false)}
        />
      )}
      {showRefund && (
        <RefundModal
          intentId={refundItem.intentId}
          amount={refundItem.amount}
          history={refundItem.history}
          emailParties={emailParties}
          onClose={() => setShowRefund(false)}
          onOk={refund}
        />
      )}
      {reallocateItem && (
        <ReallocateInvoiceModal item={reallocateItem} onOk={reallocate} onClose={() => setReallocateItem(null)} />
      )}
      {labelItem && (
        <StatusLabelModal
          item={labelItem}
          automaticLabel={(StripeStatusLabels[labelItem.status] || [])[1] || labelItem.status}
          onOk={saveStatusLabel}
          onClose={() => setLabelItem(null)}
        />
      )}
      {isOpen && (
        <InvoicePaymentModal
          payerEmail={borrower.email}
          refId={setLoanNumber()}
          showDescription={false}
          onClose={onCloseModal}
        />
      )}
    </div>
  )
}

function StatusLabelModal({
  item,
  automaticLabel,
  onOk,
  onClose,
}: {
  item: InvoiceLine
  automaticLabel: string
  onOk: (intentId: string, statusLabel: string) => Promise<void>
  onClose: () => void
}) {
  const [value, setValue] = useState(item.statusLabel || '')
  const [saving, setSaving] = useState(false)

  const onSave = async () => {
    setSaving(true)
    try {
      await onOk(item.intentId, value)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title="Status label" isOpen loading={saving} onOk={onSave} onClose={onClose} titleOkay="Save">
      <div className="w-[calc(100vw-5rem)] max-w-[28rem]">
        <p className="text-sm text-gray-600 mb-3">
          This changes the words in the Status column. The payment status stays {automaticLabel}.
        </p>
        <input
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          value={value}
          maxLength={80}
          autoFocus
          onChange={(event) => setValue(event.target.value)}
        />
        <p className="text-xs text-gray-500 mt-2">Leave blank to show {automaticLabel} again.</p>
      </div>
    </Modal>
  )
}
